import React, { createContext, useState, useContext } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const SnackbarContext = createContext();

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success', 'error', 'warning', 'info'
  });

  const [confirm, setConfirm] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    onConfirm: null,
    onCancel: null,
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const showConfirm = (message, onConfirm, title = 'Confirm') => {
    return new Promise((resolve) => {
      setConfirm({
        open: true,
        title,
        message,
        onConfirm: () => {
          setConfirm({ ...confirm, open: false });
          resolve(true);
          if (onConfirm) onConfirm();
        },
        onCancel: () => {
          setConfirm({ ...confirm, open: false });
          resolve(false);
        },
      });
    });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirmClose = () => {
    if (confirm.onCancel) {
      confirm.onCancel();
    } else {
      setConfirm({ ...confirm, open: false });
    }
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, showConfirm }}>
      {children}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ marginTop: '70px' }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirm.open}
        onClose={handleConfirmClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">{confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirm.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirm.onCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={confirm.onConfirm} color="primary" variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </SnackbarContext.Provider>
  );
};
